// Random, persistent device id. IndexedDB (with try/catch everywhere).
// If storage is unavailable the id lives only for this session — the server
// simply sees a new device on the next run (documented limitation).

const DEVICE_KEY = 'device_id';
const DB_NAME = 'ildc-license';
const STORE = 'kv';

function idb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(null);
      const open = indexedDB.open(DB_NAME, 1);
      open.onupgradeneeded = () => {
        open.result.createObjectStore(STORE);
      };
      open.onsuccess = () => resolve(open.result);
      open.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function idbGet(key: string): Promise<string | null> {
  const db = await idb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(key);
      req.onsuccess = () => resolve((req.result as string) ?? null);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function idbSet(key: string, value: string): Promise<void> {
  const db = await idb();
  if (!db) return;
  try {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(value, key);
  } catch {
    /* storage unavailable */
  }
}

function randomId(): string {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Returns the stable device id (or a fresh one if none exists). */
export async function getOrCreateDeviceId(): Promise<string> {
  const existing = await idbGet(DEVICE_KEY);
  if (existing) return existing;
  const id = randomId();
  await idbSet(DEVICE_KEY, id);
  return id;
}

export async function loadToken(): Promise<string | null> {
  return idbGet('license_token');
}

export async function saveToken(token: string): Promise<void> {
  await idbSet('license_token', token);
}

/** Last seen SERVER time (token iat) for the clock-tamper check (#8). */
export async function loadLastSeenServerTime(): Promise<number | undefined> {
  const v = await idbGet('last_seen_server_time');
  if (!v) return undefined;
  const t = Number(v);
  return Number.isFinite(t) ? t : undefined;
}

export async function saveLastSeenServerTime(epochSeconds: number): Promise<void> {
  if (Number.isFinite(epochSeconds)) await idbSet('last_seen_server_time', String(epochSeconds));
}
